import { Box, ButtonBase, Typography, Badge } from '@mui/material'
import { FilterList as SortIcon, Tune } from '@mui/icons-material'
import React from 'react'

interface FloatingFilterBarProps {
    filtersCount: number
    nonStopEnabled: boolean
    nonStopChecked: boolean
    onFilterClick?: () => void
    onSortClick?: () => void
    onNonStopClick?: () => void
    filterLabel: string
    sortLabel: string
    nonStopLabel: string
}

const FloatingFilterBar: React.FC<FloatingFilterBarProps> = ({
    filtersCount,
    nonStopEnabled,
    nonStopChecked,
    onFilterClick,
    onSortClick,
    onNonStopClick,
    filterLabel,
    sortLabel,
    nonStopLabel
}) => {
    const hasFiltersApplied = filtersCount > 0

    return (
        <Box
            sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: theme => theme.zIndex.appBar + 1,
                px: 2,
                pb: theme => `calc(${theme.spacing(1.5)} + env(safe-area-inset-bottom))`,
                pt: 1.5,
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                pointerEvents: 'none'
            }}
        >
        <Box
            sx={{
                pointerEvents: 'auto',
                width: 232,
                maxWidth: '100%',
                height: 61,
                borderRadius: 1.5,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: 3,
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                px: 1,
                py: 1,
                gap: 0.5
            }}
        >
                <ButtonBase
                    onClick={onFilterClick}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1.5,
                        height: '100%'
                    }}
                >
                    <Badge
                        color="primary"
                        badgeContent={filtersCount}
                        invisible={!hasFiltersApplied}
                        overlap="circular"
                    >
                        <Tune fontSize="small" />
                    </Badge>
                    <Typography variant="caption" sx={{ fontWeight: 500, mt: 0.5 }}>
                        {filterLabel}
                    </Typography>
                </ButtonBase>

                {nonStopEnabled && (
                    <ButtonBase
                        onClick={onNonStopClick}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1.5,
                            height: '100%'
                        }}
                    >
                        <Box
                            sx={theme => ({
                                width: 28,
                                height: 16,
                                borderRadius: 999,
                                display: 'flex',
                                alignItems: 'center',
                                px: 0.3,
                                justifyContent: nonStopChecked ? 'flex-end' : 'flex-start',
                                mb: 0.25,
                                bgcolor: nonStopChecked ? theme.palette.primary.main : theme.palette.background.paper,
                                border: `1px solid ${
                                    theme.palette.textColors?.secondary || theme.palette.text.secondary
                                }`
                            })}
                        >
                            <Box
                                sx={theme => ({
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor:
                                        theme.palette.textColors?.secondary || theme.palette.text.secondary,
                                    backgroundColor:
                                        nonStopChecked ? theme.palette.common.white : undefined
                                })}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 500, mt: 0.5 }}>
                            {nonStopLabel}
                        </Typography>
                    </ButtonBase>
                )}

                <ButtonBase
                    onClick={onSortClick}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1.5,
                        height: '100%'
                    }}
                >
                    <SortIcon fontSize="small" />
                    <Typography variant="caption" sx={{ fontWeight: 500, mt: 0.5 }}>
                        {sortLabel}
                    </Typography>
                </ButtonBase>
            </Box>
        </Box>
    )
}

export default FloatingFilterBar

